import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InviteFriendsModal = ({ 
  isOpen, 
  onClose, 
  sessionId,
  sessionName,
  userToken
}) => {
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviting, setInviting] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchOnlineFriends();
    }
  }, [isOpen]);

  const fetchOnlineFriends = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/friends/online`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      
      setOnlineFriends(response.data.online_friends || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch online friends:', err);
      setError(err.response?.data?.detail || 'Failed to load online friends');
      setLoading(false);
    }
  };

  const handleInvite = async (friendUserId, friendName) => {
    setInviting(prev => ({ ...prev, [friendUserId]: true }));
    
    try {
      await axios.post(
        `${API}/jam/${sessionId}/invite`,
        { invited_user_id: friendUserId },
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      
      alert(`✅ Invitation sent to ${friendName}!`);
      
      // Remove invited friend from list
      setOnlineFriends(prev => prev.filter(f => f.user_id !== friendUserId));
    } catch (err) {
      console.error('Failed to send invitation:', err);
      alert(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(prev => ({ ...prev, [friendUserId]: false }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="jam-modal-overlay" onClick={onClose}>
      <div className="jam-modal-content invite-friends-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jam-modal-header">
          <h2>👥 Invite Friends to Jam</h2>
          <button className="jam-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="jam-modal-body">
          <p className="invite-session-name">
            Session: <strong>{sessionName}</strong>
          </p>

          {loading && (
            <div className="invite-loading">
              <div className="loading-spinner"></div>
              <p>Finding online friends...</p>
            </div>
          )}

          {error && (
            <div className="invite-error">
              <p>❌ {error}</p>
              <button className="jam-btn jam-btn-secondary jam-btn-small" onClick={fetchOnlineFriends}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && onlineFriends.length === 0 && (
            <div className="invite-empty">
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>😴</p>
              <p>No friends are currently online</p>
              <p className="invite-help-text">They'll be able to join when they're online!</p>
            </div>
          )}

          {!loading && !error && onlineFriends.length > 0 && (
            <div className="invite-friends-list">
              {onlineFriends.map((friend) => (
                <div key={friend.user_id} className="invite-friend-item">
                  <div className="invite-friend-info">
                    {friend.profile_picture ? (
                      <img 
                        src={friend.profile_picture} 
                        alt={friend.username}
                        className="invite-friend-avatar"
                      />
                    ) : (
                      <div className="invite-friend-avatar-placeholder">
                        {friend.username?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="invite-friend-details">
                      <h4>{friend.username}</h4>
                      <span className="invite-friend-status">🟢 Online</span>
                    </div>
                  </div>
                  <button
                    className="jam-btn jam-btn-primary jam-btn-small"
                    onClick={() => handleInvite(friend.user_id, friend.username)}
                    disabled={inviting[friend.user_id]}
                  >
                    {inviting[friend.user_id] ? 'Inviting...' : 'Invite'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="jam-modal-footer">
          <button className="jam-btn jam-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteFriendsModal;
